import type { Props as InputProps } from 'components/Input';
import Input from 'components/Input';
import React, { useCallback, useEffect, useLayoutEffect, useState } from 'react';
import type { Merge } from 'types/utility';

interface OwnProps {
  value: number | null | undefined;
  onChange: (value: number) => void;
  tag?: string | React.ComponentType;
}
export type Props = Merge<Omit<InputProps, 'type'>, OwnProps>;

let scrollListenerRefCount = 0;
const scrollListener = (e: WheelEvent) => {
  if ((e.target as HTMLElement).nodeName === 'INPUT' && document.activeElement === e.target) {
    e.preventDefault();
  }
};

const NumberInput = React.forwardRef<HTMLInputElement, Props>((props, ref) => {
  const {
    value: numericValue,
    onChange: numericOnChange,
    onBlur,
    tag: Tag = Input,
    ...rest
  } = props;

  // Keep a nullable undefined value in state - this is the source of truth
  const [stateValue, setValueInState] = useState<string | undefined>(
    props.value == null ? undefined : String(props.value)
  );

  const onChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      // If the input is emptied, set value to undefined and propagate 0
      const value = e.target.value;
      setValueInState(value.length === 0 ? undefined : value);

      if (typeof stateValue === 'undefined' || +stateValue !== +value) {
        numericOnChange(+value);
      }
    },
    [numericOnChange, stateValue]
  );

  const handleBlur = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      // If the state value is undefined, reset on blur
      if (numericValue != null) {
        setValueInState(String(numericValue));
      }
      // Propagate event
      if (onBlur) {
        onBlur(e);
      }
    },
    [numericValue, onBlur]
  );

  // Accept changes to our state from props to keep in sync
  const receiveProps = useCallback(() => {
    const propValueIsZeroButFieldIsEmpty = numericValue === 0 && stateValue == null;
    if (propValueIsZeroButFieldIsEmpty) {
      // Let the user have an empty input field
      return;
    }

    // We explicitly want to allow string coercion here:
    // biome-ignore lint/suspicious/noDoubleEquals: intentional loose comparison
    if (numericValue != stateValue) {
      // Accept change
      setValueInState(numericValue == null ? undefined : String(numericValue));
    }
  }, [numericValue, stateValue]);
  // Fire function when props.value changes
  useEffect(receiveProps, [receiveProps]);

  useLayoutEffect(() => {
    if (++scrollListenerRefCount === 1) {
      document.addEventListener('wheel', scrollListener, { passive: false });
    }
    return () => {
      if (--scrollListenerRefCount === 0) {
        document.removeEventListener('wheel', scrollListener);
      }
    };
  }, []);

  return (
    <Tag
      {...rest}
      type="number"
      ref={ref}
      onChange={onChange}
      value={stateValue == null ? '' : stateValue}
      onBlur={handleBlur}
    />
  );
});
export default NumberInput;
